import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBuilding,
  faChartLine,
  faChevronRight,
  faClock,
  faDesktop,
  faFileCsv,
  faFileExcel,
  faFileMedical,
  faFilePdf,
  faFileWaveform,
  faHandshake,
  faHospital,
  faMobileScreenButton,
  faQrcode,
  faStethoscope,
  faSyncAlt,
  faTriangleExclamation,
  faUserDoctor,
  faUserGroup,
} from "@fortawesome/free-solid-svg-icons";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import axios from "axios";
import { Chart } from "chart.js";
import {
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  BarController,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { RateLimitAlertContext } from "../contexts/RateLimitAlertContext";
import { AdminAccessAlertContext } from "../contexts/AdminAccessAlertContext";
import { BASE_URL } from "../config";
import clsx from "clsx";
import { useNavigate } from "react-router-dom";

Chart.register(
  CategoryScale,
  LinearScale,
  BarController,
  BarElement,
  ArcElement,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
  Filler
);

const metricCards = [
  {
    icon: faUserGroup,
    title: "Active Members",
    metricKey: "Active Members",
    path: "/members",
    label: "Eligible lives",
    gradient: "from-teal-600 via-emerald-600 to-cyan-600",
    shadow: "shadow-teal-500/20",
  },
  {
    icon: faHospital,
    title: "Accredited Providers",
    metricKey: "Accredited Providers",
    path: "/medical/provider",
    label: "Active facilities",
    gradient: "from-blue-600 via-sky-600 to-cyan-500",
    shadow: "shadow-blue-500/20",
  },
  {
    icon: faUserDoctor,
    title: "Active Physicians",
    metricKey: "Active Physicians",
    path: "/physician",
    label: "Accredited doctors",
    gradient: "from-violet-600 via-indigo-600 to-blue-600",
    shadow: "shadow-indigo-500/20",
  },
  {
    icon: faFileMedical,
    title: "Issued LOA",
    metricKey: "Issued LOA",
    fallbackKey: "Issued Loa",
    path: "/dashboard",
    label: "Today",
    gradient: "from-amber-500 via-orange-500 to-rose-500",
    shadow: "shadow-orange-500/20",
  },
  {
    icon: faFileWaveform,
    title: "Weekly LOA",
    metricKey: "Weekly LOA",
    fallbackKey: "Weekly Loa",
    path: "/dashboard",
    label: "Rolling window",
    gradient: "from-rose-600 via-pink-600 to-fuchsia-600",
    shadow: "shadow-rose-500/20",
  },
  {
    icon: faHandshake,
    title: "Physicians With Affiliations",
    metricKey: "Physicians With Affiliations",
    path: "/physician",
    label: "Provider links",
    gradient: "from-slate-700 via-slate-600 to-teal-700",
    shadow: "shadow-slate-500/20",
  },
];

const defaultPlatformRows = [
  { Platform: "Web Portal", TRN_Count: 0 },
  { Platform: "Mobile App", TRN_Count: 0 },
  { Platform: "Provider Access", TRN_Count: 0 },
  { Platform: "Provider QR", TRN_Count: 0 },
];

const dashboardStorageKeys = {
  loaTrendView: "allaccess.dashboard.loaTrendView",
  platformDateFrom: "allaccess.dashboard.platformDateFrom",
  platformDateTo: "allaccess.dashboard.platformDateTo",
};

const platformChartColors = ["#2563eb", "#0f766e", "#7c3aed", "#d97706"];

const platformIconMap = {
  "Web Portal": {
    icon: faDesktop,
    className: "bg-blue-50 text-blue-700 ring-blue-100",
  },
  "Mobile App": {
    icon: faMobileScreenButton,
    className: "bg-teal-50 text-teal-700 ring-teal-100",
  },
  "Provider Access": {
    icon: faHospital,
    className: "bg-violet-50 text-violet-700 ring-violet-100",
  },
  "Provider QR": {
    icon: faQrcode,
    className: "bg-amber-50 text-amber-700 ring-amber-100",
  },
};

const toYmd = (date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getCurrentMonthRange = () => {
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), 1);
  const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);

  return {
    date_from: toYmd(start),
    date_to: toYmd(end),
  };
};

const isYmd = (value) => /^\d{4}-\d{2}-\d{2}$/.test(String(value || ""));

const getStoredPlatformDateRange = () => {
  const fallback = getCurrentMonthRange();

  if (typeof window === "undefined") {
    return fallback;
  }

  const storedFrom = window.localStorage.getItem(
    dashboardStorageKeys.platformDateFrom
  );
  const storedTo = window.localStorage.getItem(
    dashboardStorageKeys.platformDateTo
  );

  return {
    date_from: isYmd(storedFrom) ? storedFrom : fallback.date_from,
    date_to: isYmd(storedTo) ? storedTo : fallback.date_to,
  };
};

const getStoredLoaTrendView = () => {
  if (typeof window === "undefined") {
    return "line";
  }

  const storedValue = window.localStorage.getItem(
    dashboardStorageKeys.loaTrendView
  );

  return ["line", "bar"].includes(storedValue) ? storedValue : "line";
};

const parseLocalYmd = (value) => {
  if (!value) return null;
  const [year, month, day] = String(value).slice(0, 10).split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const formatCompactDate = (date) =>
  date.toLocaleDateString("en-PH", {
    month: "short",
    day: "numeric",
  });

const formatNumber = (value) => {
  const numericValue = Number(value || 0);
  return Number.isFinite(numericValue)
    ? numericValue.toLocaleString("en-US")
    : "0";
};

const formatDateLabel = (value) => {
  const date = parseLocalYmd(value);
  if (!date) return "-";
  return date.toLocaleDateString("en-PH", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatDateTimeLabel = (date) => {
  if (!date) return "Pending refresh";
  return date.toLocaleString("en-PH", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const escapeCsvValue = (value) => {
  const stringValue = String(value ?? "");
  return `"${stringValue.replace(/"/g, '""')}"`;
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const downloadTextFile = (filename, content, mimeType) => {
  const blob = new Blob([content], { type: mimeType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const DashboardHome_v3 = () => {
  const [metrics, setMetrics] = useState({});
  const [chartData, setChartData] = useState(null);
  const [barChartData, setBarChartData] = useState(null);
  const [loaTrendRows, setLoaTrendRows] = useState([]);
  const [loaTrendView, setLoaTrendView] = useState(() =>
    getStoredLoaTrendView()
  );
  const [platformCounts, setPlatformCounts] = useState(defaultPlatformRows);
  const [platformDateRange, setPlatformDateRange] = useState(() =>
    getStoredPlatformDateRange()
  );
  const [appliedPlatformDateRange, setAppliedPlatformDateRange] = useState(() =>
    getStoredPlatformDateRange()
  );
  const [platformCountMeta, setPlatformCountMeta] = useState({
    date_from: "",
    date_to: "",
    total: 0,
  });
  const [loading, setLoading] = useState(true);
  const [timeFrame, setTimeFrame] = useState("31 DAY");
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [nearExpiryAlert, setNearExpiryAlert] = useState({
    near_expiry_count: 0,
    threshold: 50,
    critical_days: 3,
    grace_days: 5,
    should_alert: false,
    top_records: [],
    generated_at: null,
  });
  const [nearExpiryDismissed, setNearExpiryDismissed] = useState(false);
  const { openAlert: openRateLimitAlert } = useContext(RateLimitAlertContext);
  const { openAlert: openAdminAccessAlert } = useContext(
    AdminAccessAlertContext
  );
  const navigate = useNavigate();
  const loaTrendChartRef = useRef(null);

  const prepareChartData = (stats, period) => {
    const days = parseInt(period.replace(" DAY", ""), 10);
    const rows = (Array.isArray(stats) ? stats : [])
      .map((item) => ({
        date: String(item.date || "").slice(0, 10),
        loa_count: Number(item.loa_count || 0),
      }))
      .filter((item) => isYmd(item.date))
      .slice(-days);
    const labels = rows.map((item) => formatCompactDate(parseLocalYmd(item.date)));
    const loaCounts = rows.map((item) => item.loa_count);

    setLoaTrendRows(rows);

    setChartData({
      labels,
      datasets: [
        {
          label: "LOA Requests",
          data: loaCounts,
          borderColor: "#0f766e",
          backgroundColor: "rgba(15, 118, 110, 0.12)",
          tension: 0.38,
          borderWidth: 3,
          pointRadius: 3,
          pointHoverRadius: 6,
          pointHitRadius: 18,
          pointBackgroundColor: "#ffffff",
          pointBorderColor: "#0f766e",
          pointBorderWidth: 2,
          fill: true,
        },
      ],
    });

    setBarChartData({
      labels,
      datasets: [
        {
          label: "LOA Requests",
          data: loaCounts,
          backgroundColor: "#0f766e",
          borderRadius: 5,
          maxBarThickness: 28,
        },
      ],
    });
  };

  const fetchData = useCallback(
    async (period) => {
      setLoading(true);
      try {
        const token = localStorage.getItem("authentication/token");
        const headers = {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        };

        const [metricsRes, statsRes, platformCountsRes, nearExpiryRes] =
          await Promise.allSettled([
            axios.get(`${BASE_URL}/dashboard/metrics`, { headers }),
            axios.get(`${BASE_URL}/dashboard/request-stats`, {
              headers,
              params: { timeFrame: period },
            }),
            axios.get(`${BASE_URL}/dashboard/platform-transaction-counts`, {
              headers,
              params: {
                start_date: appliedPlatformDateRange.date_from,
                end_date: appliedPlatformDateRange.date_to,
              },
            }),
            axios.get(`${BASE_URL}/dashboard/claims-near-expiry-alert`, {
              headers,
              params: {
                threshold: 50,
                critical_days: 3,
              },
            }),
          ]);

        if (metricsRes.status !== "fulfilled" || statsRes.status !== "fulfilled") {
          throw (
            metricsRes.status === "rejected" ? metricsRes.reason : statsRes.reason
          );
        }

        setMetrics(metricsRes.value.data || {});
        prepareChartData(statsRes.value.data, period);
        setLastUpdatedAt(new Date());

        if (platformCountsRes.status === "fulfilled") {
          const platformPayload = platformCountsRes.value?.data || {};
          setPlatformCounts(
            Array.isArray(platformPayload.data)
              ? platformPayload.data
              : defaultPlatformRows
          );
          setPlatformCountMeta({
            date_from: platformPayload.date_from || "",
            date_to: platformPayload.date_to || "",
            total: Number(platformPayload.total || 0),
          });
        } else {
          console.error(
            "Error fetching platform transaction counts:",
            platformCountsRes.reason
          );
          setPlatformCounts(defaultPlatformRows);
          setPlatformCountMeta({ date_from: "", date_to: "", total: 0 });
        }

        if (nearExpiryRes.status === "fulfilled") {
          const alertPayload = nearExpiryRes.value?.data?.data || {};
          setNearExpiryAlert({
            near_expiry_count: Number(alertPayload.near_expiry_count || 0),
            threshold: Number(alertPayload.threshold || 50),
            critical_days: Number(alertPayload.critical_days || 3),
            grace_days: Number(alertPayload.grace_days || 5),
            should_alert: Boolean(alertPayload.should_alert),
            top_records: Array.isArray(alertPayload.top_records)
              ? alertPayload.top_records.slice(0, 3)
              : [],
            generated_at: alertPayload.generated_at || null,
          });
          setNearExpiryDismissed(false);
        } else {
          console.error("Error fetching near-expiry alert data:", nearExpiryRes.reason);
          setNearExpiryAlert((previous) => ({
            ...previous,
            should_alert: false,
            near_expiry_count: 0,
            top_records: [],
          }));
        }
      } catch (error) {
        if (error.response?.status === 401) window.location.href = "/login";
        if (error.response?.status === 403) openAdminAccessAlert();
        if (error.response?.status === 429) openRateLimitAlert();
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    },
    [appliedPlatformDateRange, openAdminAccessAlert, openRateLimitAlert]
  );

  useEffect(() => {
    fetchData(timeFrame);
  }, [fetchData, timeFrame]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(dashboardStorageKeys.loaTrendView, loaTrendView);
  }, [loaTrendView]);

  const handleApplyPlatformDateRange = () => {
    if (!isYmd(platformDateRange.date_from) || !isYmd(platformDateRange.date_to)) {
      return;
    }

    if (parseLocalYmd(platformDateRange.date_to) < parseLocalYmd(platformDateRange.date_from)) {
      return;
    }

    if (typeof window !== "undefined") {
      window.localStorage.setItem(
        dashboardStorageKeys.platformDateFrom,
        platformDateRange.date_from
      );
      window.localStorage.setItem(
        dashboardStorageKeys.platformDateTo,
        platformDateRange.date_to
      );
    }

    setAppliedPlatformDateRange(platformDateRange);
  };

  const handleResetPlatformDateRange = () => {
    const currentMonthRange = getCurrentMonthRange();
    setPlatformDateRange(currentMonthRange);

    if (typeof window !== "undefined") {
      window.localStorage.setItem(
        dashboardStorageKeys.platformDateFrom,
        currentMonthRange.date_from
      );
      window.localStorage.setItem(
        dashboardStorageKeys.platformDateTo,
        currentMonthRange.date_to
      );
    }

    setAppliedPlatformDateRange(currentMonthRange);
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    events: [],
    interaction: {
      mode: "nearest",
      axis: "x",
      intersect: false,
    },
    hover: {
      mode: "nearest",
      axis: "x",
      intersect: false,
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: "#0f172a",
        titleColor: "#e2e8f0",
        bodyColor: "#f8fafc",
        padding: 12,
        cornerRadius: 8,
        titleFont: {
          size: 12,
          family: "Inter, sans-serif",
        },
        bodyFont: {
          size: 13,
          family: "Inter, sans-serif",
        },
        displayColors: false,
        position: "nearest",
      },
    },
    layout: {
      padding: {
        left: 8,
        right: 18,
      },
    },
    scales: {
      x: {
        offset: false,
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          color: "#64748b",
          font: {
            size: 11,
            family: "Inter, sans-serif",
          },
          maxRotation: 0,
          autoSkip: true,
        },
        border: {
          display: false,
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: "#eef2f7",
          drawBorder: false,
        },
        ticks: {
          color: "#64748b",
          font: {
            size: 11,
            family: "Inter, sans-serif",
          },
          precision: 0,
        },
        border: {
          display: false,
        },
      },
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          color: "#475569",
          font: {
            size: 11,
            family: "Inter, sans-serif",
          },
          usePointStyle: true,
        },
      },
      tooltip: {
        backgroundColor: "#0f172a",
        titleColor: "#e2e8f0",
        bodyColor: "#f8fafc",
        displayColors: false,
      },
    },
    cutout: "64%",
  };

  const clearLoaTrendActivePoint = useCallback(() => {
    const chart = loaTrendChartRef.current;
    if (!chart) return;

    chart.setActiveElements([]);
    chart.tooltip?.setActiveElements([], { x: 0, y: 0 });
    chart.update("none");
  }, []);

  const handleLoaTrendPointerMove = useCallback(
    (event) => {
      const chart = loaTrendChartRef.current;
      const canvas = chart?.canvas;
      const xScale = chart?.scales?.x;

      if (!chart || !canvas || !xScale || loaTrendRows.length === 0) {
        return;
      }

      const rect = canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) return;

      const x = (event.clientX - rect.left) * (chart.width / rect.width);
      const y = (event.clientY - rect.top) * (chart.height / rect.height);
      const chartArea = chart.chartArea || {};
      const edgeTolerance = 14;

      if (
        x < chartArea.left - edgeTolerance ||
        x > chartArea.right + edgeTolerance ||
        y < chartArea.top - edgeTolerance ||
        y > chartArea.bottom + edgeTolerance
      ) {
        clearLoaTrendActivePoint();
        return;
      }

      let nearestIndex = 0;
      let nearestDistance = Number.POSITIVE_INFINITY;

      loaTrendRows.forEach((_, index) => {
        const pointX = xScale.getPixelForValue(index);
        const distance = Math.abs(pointX - x);

        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });

      const activeElement = { datasetIndex: 0, index: nearestIndex };
      const visualElement = chart.getDatasetMeta(0)?.data?.[nearestIndex];
      const tooltipPosition = visualElement?.tooltipPosition
        ? visualElement.tooltipPosition()
        : {
            x: xScale.getPixelForValue(nearestIndex),
            y: chartArea.top,
          };

      chart.setActiveElements([activeElement]);
      chart.tooltip?.setActiveElements([activeElement], tooltipPosition);
      chart.update("none");
    },
    [clearLoaTrendActivePoint, loaTrendRows]
  );

  const getMetricValue = (card) =>
    metrics[card.metricKey] ?? metrics[card.fallbackKey] ?? 0;

  const platformDateRangeInvalid =
    !isYmd(platformDateRange.date_from) ||
    !isYmd(platformDateRange.date_to) ||
    parseLocalYmd(platformDateRange.date_to) <
      parseLocalYmd(platformDateRange.date_from);

  const getPlatformRowsForExport = () =>
    platformCounts.map((row) => ({
      Platform: row.Platform || row.platform || "",
      "Transaction Count": Number(row.TRN_Count ?? row.transaction_count ?? 0),
    }));

  const platformDoughnutData = useMemo(
    () => ({
      labels: platformCounts.map((row) => row.Platform || row.platform || ""),
      datasets: [
        {
          data: platformCounts.map((row) =>
            Number(row.TRN_Count ?? row.transaction_count ?? 0)
          ),
          backgroundColor: platformChartColors,
          borderColor: "#ffffff",
          borderWidth: 2,
        },
      ],
    }),
    [platformCounts]
  );

  const platformDateRangeLabel =
    platformCountMeta.date_from && platformCountMeta.date_to
      ? `${formatDateLabel(platformCountMeta.date_from)} to ${formatDateLabel(
          platformCountMeta.date_to
        )}`
      : "Current month";

  const platformExportBaseName = `platform_transaction_counts_${
    platformCountMeta.date_from || "current"
  }_${platformCountMeta.date_to || "month"}`;

  const handleExportPlatformCsv = () => {
    const rows = getPlatformRowsForExport();
    const csvRows = [
      ["Platform", "Transaction Count"].map(escapeCsvValue).join(","),
      ...rows.map((row) =>
        [row.Platform, row["Transaction Count"]].map(escapeCsvValue).join(",")
      ),
    ];

    downloadTextFile(
      `${platformExportBaseName}.csv`,
      csvRows.join("\r\n"),
      "text/csv;charset=utf-8;"
    );
  };

  const handleExportPlatformExcel = () => {
    const rows = getPlatformRowsForExport();
    const tableRows = rows
      .map(
        (row) => `
          <tr>
            <td>${escapeHtml(row.Platform)}</td>
            <td style="text-align:right">${escapeHtml(row["Transaction Count"])}</td>
          </tr>
        `
      )
      .join("");

    const html = `
      <html>
        <head>
          <meta charset="UTF-8" />
        </head>
        <body>
          <h3>Platform Transaction Counts</h3>
          <p>${escapeHtml(platformDateRangeLabel)}</p>
          <table border="1">
            <thead>
              <tr>
                <th>Platform</th>
                <th>Transaction Count</th>
              </tr>
            </thead>
            <tbody>${tableRows}</tbody>
          </table>
        </body>
      </html>
    `;

    downloadTextFile(
      `${platformExportBaseName}.xls`,
      html,
      "application/vnd.ms-excel;charset=utf-8;"
    );
  };

  const handleExportPlatformPdf = () => {
    const rows = getPlatformRowsForExport();
    const tableRows = rows
      .map(
        (row) => `
          <tr>
            <td>${escapeHtml(row.Platform)}</td>
            <td class="count">${escapeHtml(row["Transaction Count"])}</td>
          </tr>
        `
      )
      .join("");

    const printable = window.open("", "_blank", "width=960,height=720");
    if (!printable) return;

    printable.document.write(`
      <html>
        <head>
          <title>Platform Transaction Counts</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              color: #0f172a;
              margin: 32px;
            }
            h1 {
              margin: 0;
              font-size: 20px;
            }
            p {
              color: #475569;
              font-size: 12px;
              margin: 6px 0 20px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              font-size: 13px;
            }
            th {
              background: #f1f5f9;
              color: #334155;
              text-align: left;
              padding: 10px;
              border: 1px solid #cbd5e1;
            }
            td {
              padding: 10px;
              border: 1px solid #cbd5e1;
            }
            .count {
              text-align: right;
              font-weight: 700;
            }
          </style>
        </head>
        <body>
          <h1>Platform Transaction Counts</h1>
          <p>${escapeHtml(platformDateRangeLabel)}</p>
          <table>
            <thead>
              <tr>
                <th>Platform</th>
                <th>Transaction Count</th>
              </tr>
            </thead>
            <tbody>${tableRows}</tbody>
          </table>
        </body>
      </html>
    `);
    printable.document.close();
    printable.focus();
    printable.print();
  };

  const LoadingSkeleton = () => (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-6">
        {metricCards.map((card) => (
          <div
            key={card.title}
            className="h-36 animate-pulse rounded-lg border border-slate-200 bg-white"
          />
        ))}
      </div>
      <div className="h-[22rem] animate-pulse rounded-lg border border-slate-200 bg-white" />
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-5 md:px-6">
      <link
        href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap"
        rel="stylesheet"
      />

      <div className="mx-auto max-w-1xl space-y-5">
        <section className="rounded-lg border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-col gap-4 border-l-4 border-teal-600 p-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex min-w-0 items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-teal-50 text-teal-700 ring-1 ring-teal-100">
                <FontAwesomeIcon icon={faStethoscope} className="text-lg" />
              </div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-2xl font-bold tracking-tight text-slate-900">
                    AllAccess Medical Dashboard
                  </h1>
                  <span className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                    <span className="h-2 w-2 rounded-full bg-emerald-500" />
                    Version 2.0 Live
                  </span>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-3 text-xs font-medium text-slate-500">
                  <span className="inline-flex items-center gap-1.5">
                    <FontAwesomeIcon icon={faClock} className="text-slate-400" />
                    Last refreshed: {formatDateTimeLabel(lastUpdatedAt)}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <FontAwesomeIcon icon={faFileWaveform} className="text-teal-600" />
                    LOA trend: {timeFrame === "7 DAY" ? "7 days" : "31 days"}
                  </span>
                </div>
              </div>
            </div>

            <button
              onClick={() => fetchData(timeFrame)}
              disabled={loading}
              aria-busy={loading}
              className={clsx(
                "inline-flex items-center justify-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-semibold shadow-sm transition",
                loading
                  ? "border-slate-200 bg-slate-100 text-slate-500"
                  : "border-teal-200 bg-teal-600 text-white hover:bg-teal-700"
              )}
            >
              <FontAwesomeIcon
                icon={faSyncAlt}
                className={clsx("text-sm", loading && "animate-spin")}
              />
              {loading ? "Refreshing" : "Refresh"}
            </button>
          </div>
        </section>

        {loading ? (
          <LoadingSkeleton />
        ) : (
          <>
            <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-6">
              {metricCards.map((card) => (
                <button
                  key={card.title}
                  type="button"
                  onClick={() => navigate(card.path)}
                  className={clsx(
                    "group relative min-h-36 overflow-hidden rounded-lg border border-white/20 bg-gradient-to-br bg-[length:180%_180%] bg-left-top p-4 text-left shadow-lg transition duration-300 hover:-translate-y-0.5 hover:bg-right-bottom hover:shadow-xl",
                    card.gradient,
                    card.shadow
                  )}
                >
                  <FontAwesomeIcon
                    icon={card.icon}
                    className="pointer-events-none absolute -right-5 bottom-1 text-[6.5rem] text-white opacity-15 transition duration-300 group-hover:scale-110 group-hover:opacity-20"
                  />
                  <div className="absolute inset-x-0 top-0 h-1 bg-white/30" />

                  <div className="relative z-10 flex items-start justify-between gap-3">
                    <div
                      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-white/20 text-white ring-1 ring-white/25 backdrop-blur-sm"
                    >
                      <FontAwesomeIcon icon={card.icon} className="text-lg" />
                    </div>
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/15 text-white/80 ring-1 ring-white/20 transition group-hover:bg-white/25 group-hover:text-white">
                      <FontAwesomeIcon icon={faChevronRight} className="text-xs" />
                    </span>
                  </div>

                  <div className="relative z-10 mt-4">
                    <p className="text-xs font-semibold uppercase text-white/75">
                      {card.label}
                    </p>
                    <p className="mt-1 text-3xl font-bold tracking-tight text-white">
                      {formatNumber(getMetricValue(card))}
                    </p>
                    <h2 className="mt-2 min-h-[2.25rem] text-sm font-semibold leading-snug text-white/90">
                      {card.title}
                    </h2>
                  </div>
                </button>
              ))}
            </section>

            <section className="grid grid-cols-1 gap-5 xl:grid-cols-3">
              <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm xl:col-span-2">
                <div className="mb-5 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-teal-50 text-teal-700 ring-1 ring-teal-100">
                      <FontAwesomeIcon icon={faChartLine} />
                    </div>
                    <div>
                      <h2 className="text-base font-bold text-slate-900">
                        LOA Request Trend
                      </h2>
                      <p className="text-xs font-medium text-slate-500">
                        Daily request volume across {loaTrendRows.length} day(s)
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <div className="inline-flex w-fit rounded-lg bg-slate-100 p-1">
                      {[
                        { key: "line", label: "Line" },
                        { key: "bar", label: "Daily Bars" },
                      ].map((view) => (
                        <button
                          key={view.key}
                          type="button"
                          onClick={() => setLoaTrendView(view.key)}
                          className={clsx(
                            "rounded-md px-3 py-1.5 text-xs font-semibold transition",
                            loaTrendView === view.key
                              ? "bg-white text-teal-700 shadow-sm"
                              : "text-slate-600 hover:text-slate-900"
                          )}
                        >
                          {view.label}
                        </button>
                      ))}
                    </div>
                    <div className="inline-flex w-fit rounded-lg bg-slate-100 p-1">
                      {["7 DAY", "31 DAY"].map((frame) => (
                        <button
                          key={frame}
                          type="button"
                          onClick={() => setTimeFrame(frame)}
                          className={clsx(
                            "rounded-md px-3 py-1.5 text-xs font-semibold transition",
                            timeFrame === frame
                              ? "bg-white text-teal-700 shadow-sm"
                              : "text-slate-600 hover:text-slate-900"
                          )}
                        >
                          {frame === "7 DAY" ? "7 Days" : "31 Days"}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div
                  className="h-80"
                  onMouseMove={handleLoaTrendPointerMove}
                  onMouseLeave={clearLoaTrendActivePoint}
                >
                  {loaTrendView === "line" && chartData ? (
                    <Line
                      ref={loaTrendChartRef}
                      data={chartData}
                      options={chartOptions}
                    />
                  ) : loaTrendView === "bar" && barChartData ? (
                    <Bar
                      ref={loaTrendChartRef}
                      data={barChartData}
                      options={chartOptions}
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center rounded-lg border border-dashed border-slate-200 text-sm font-medium text-slate-400">
                      No chart data available
                    </div>
                  )}
                </div>
              </div>

              <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-blue-700 ring-1 ring-blue-100">
                    <FontAwesomeIcon icon={faBuilding} />
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-slate-900">
                      Network Snapshot
                    </h2>
                    <p className="text-xs font-medium text-slate-500">
                      Provider and physician coverage
                    </p>
                  </div>
                </div>

                <div className="mt-5 divide-y divide-slate-100">
                  <div className="py-4 first:pt-0">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm font-semibold text-slate-600">
                        Provider Facilities
                      </span>
                      <span className="text-xl font-bold text-blue-700">
                        {formatNumber(metrics["Accredited Providers"])}
                      </span>
                    </div>
                  </div>

                  <div className="py-4">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm font-semibold text-slate-600">
                        Active Physicians
                      </span>
                      <span className="text-xl font-bold text-violet-700">
                        {formatNumber(metrics["Active Physicians"])}
                      </span>
                    </div>
                  </div>

                  <div className="py-4 last:pb-0">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm font-semibold text-slate-600">
                        Physician Affiliations
                      </span>
                      <span className="text-xl font-bold text-slate-800">
                        {formatNumber(metrics["Physicians With Affiliations"])}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="grid grid-cols-1 gap-5 xl:grid-cols-3">
              <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-blue-700 ring-1 ring-blue-100">
                    <FontAwesomeIcon icon={faChartLine} />
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-slate-900">
                      Platform Mix
                    </h2>
                    <p className="text-xs font-medium text-slate-500">
                      Overall ratio for selected period.
                    </p>
                  </div>
                </div>

                {platformCountMeta.total > 0 ? (
                  <div className="mt-5 flex justify-center" style={{ maxHeight: 240 }}>
                    <Doughnut data={platformDoughnutData} options={doughnutOptions} />
                  </div>
                ) : (
                  <div className="mt-5 flex h-52 items-center justify-center rounded-lg border border-dashed border-slate-200 text-xs font-medium text-slate-400">
                    No platform data for this period.
                  </div>
                )}
              </div>

              <div className="rounded-lg border border-slate-200 bg-white shadow-sm xl:col-span-2">
                <div className="flex flex-col gap-4 border-b border-slate-100 p-5 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-teal-50 text-teal-700 ring-1 ring-teal-100">
                    <FontAwesomeIcon icon={faDesktop} />
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-slate-900">
                      Platform Transaction Counts
                    </h2>
                    <p className="text-xs font-medium text-slate-500">
                      {platformDateRangeLabel}
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap items-end gap-2">
                  <div>
                    <label className="mb-1 block text-[11px] font-semibold uppercase text-slate-500">
                      Date From
                    </label>
                    <input
                      type="date"
                      value={platformDateRange.date_from}
                      onChange={(event) =>
                        setPlatformDateRange((previous) => ({
                          ...previous,
                          date_from: event.target.value,
                        }))
                      }
                      className="h-9 rounded-lg border border-slate-200 px-3 text-xs font-semibold text-slate-700 outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-100"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-semibold uppercase text-slate-500">
                      Date To
                    </label>
                    <input
                      type="date"
                      value={platformDateRange.date_to}
                      onChange={(event) =>
                        setPlatformDateRange((previous) => ({
                          ...previous,
                          date_to: event.target.value,
                        }))
                      }
                      className="h-9 rounded-lg border border-slate-200 px-3 text-xs font-semibold text-slate-700 outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-100"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleApplyPlatformDateRange}
                    disabled={platformDateRangeInvalid}
                    className="inline-flex h-9 items-center gap-2 rounded-lg bg-teal-600 px-3 text-xs font-semibold text-white transition hover:bg-teal-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                  >
                    Apply
                  </button>
                  <button
                    type="button"
                    onClick={handleResetPlatformDateRange}
                    className="inline-flex h-9 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                  >
                    This Month
                  </button>
                  <button
                    type="button"
                    onClick={handleExportPlatformCsv}
                    className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                  >
                    <FontAwesomeIcon icon={faFileCsv} className="text-teal-700" />
                    CSV
                  </button>
                  <button
                    type="button"
                    onClick={handleExportPlatformExcel}
                    className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                  >
                    <FontAwesomeIcon icon={faFileExcel} className="text-emerald-700" />
                    Excel
                  </button>
                  <button
                    type="button"
                    onClick={handleExportPlatformPdf}
                    className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                  >
                    <FontAwesomeIcon icon={faFilePdf} className="text-rose-700" />
                    PDF
                  </button>
                </div>
              </div>

              {platformDateRangeInvalid && (
                <div className="border-b border-amber-100 bg-amber-50 px-5 py-2 text-xs font-semibold text-amber-700">
                  Select a valid platform date range before applying.
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                    <tr>
                      <th className="px-5 py-3 text-left font-semibold">
                        Platform
                      </th>
                      <th className="px-5 py-3 text-right font-semibold">
                        Transaction Count
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {platformCounts.map((row) => {
                      const platform = row.Platform || row.platform || "";
                      const trnCount = Number(
                        row.TRN_Count ?? row.transaction_count ?? 0
                      );
                      const platformIcon =
                        platformIconMap[platform] || platformIconMap["Web Portal"];

                      return (
                        <tr key={platform} className="hover:bg-slate-50/80">
                          <td className="px-5 py-4">
                            <div className="flex items-center gap-3">
                              <span
                                className={clsx(
                                  "flex h-9 w-9 items-center justify-center rounded-lg ring-1",
                                  platformIcon.className
                                )}
                              >
                                <FontAwesomeIcon icon={platformIcon.icon} />
                              </span>
                              <span className="font-semibold text-slate-800">
                                {platform}
                              </span>
                            </div>
                          </td>
                          <td className="px-5 py-4 text-right text-xl font-bold text-slate-950">
                            {formatNumber(trnCount)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="border-t border-slate-200 bg-slate-50">
                    <tr>
                      <td className="px-5 py-3 text-sm font-bold text-slate-700">
                        Total
                      </td>
                      <td className="px-5 py-3 text-right text-lg font-bold text-teal-700">
                        {formatNumber(platformCountMeta.total)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              </div>
            </section>
          </>
        )}
      </div>

      {nearExpiryAlert.should_alert && !nearExpiryDismissed && (
        <div className="fixed bottom-5 right-5 z-40 w-[min(92vw,24rem)]">
          <div className="overflow-hidden rounded-lg border border-rose-200 bg-white shadow-xl">
            <div className="border-l-4 border-rose-500 p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 flex h-10 w-10 items-center justify-center rounded-lg bg-rose-50 text-rose-700 ring-1 ring-rose-100">
                    <FontAwesomeIcon icon={faTriangleExclamation} className="text-sm" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase text-rose-600">
                      Near-Expiry Alert
                    </p>
                    <p className="text-sm font-semibold text-slate-900">
                      {formatNumber(nearExpiryAlert.near_expiry_count)} claims are at risk
                    </p>
                    <p className="mt-1 text-xs leading-relaxed text-slate-600">
                      {nearExpiryAlert.critical_days} day(s) or less before stale deadline.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setNearExpiryDismissed(true)}
                  className="rounded-md px-2 py-1 text-xs font-semibold text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                  title="Dismiss alert"
                >
                  Dismiss
                </button>
              </div>

              <div className="mt-3 space-y-2">
                {nearExpiryAlert.top_records.length === 0 ? (
                  <p className="rounded-lg bg-rose-50 px-3 py-2 text-xs text-slate-600">
                    Alert triggered because the near-expiry count exceeded {nearExpiryAlert.threshold}.
                  </p>
                ) : (
                  nearExpiryAlert.top_records.map((record) => (
                    <div
                      key={`${record.trans_no}-${record.loa_no}`}
                      className="rounded-lg border border-rose-100 bg-rose-50/50 px-3 py-2"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="truncate text-xs font-semibold text-slate-800">
                            LOA {record.loa_no || "-"}
                          </p>
                          <p className="truncate text-[11px] text-slate-500">
                            {record.provider_name || record.provider_code || "-"}
                          </p>
                          <p className="mt-1 text-[11px] text-slate-500">
                            Received: {formatDateLabel(record.date_received)}
                          </p>
                          <p className="text-[11px] text-slate-500">
                            Deadline: {formatDateLabel(record.stale_deadline)}
                          </p>
                        </div>
                        <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
                          <FontAwesomeIcon icon={faClock} className="text-[10px]" />
                          {Number(record.days_remaining || 0)} day(s)
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="mt-3 flex items-center justify-between gap-2">
                <p className="text-[11px] font-medium text-slate-500">
                  Trigger: more than {nearExpiryAlert.threshold} claims
                </p>
                <button
                  type="button"
                  onClick={() => navigate("/claims/stale-checker")}
                  className="inline-flex items-center gap-2 rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-rose-700"
                >
                  <FontAwesomeIcon icon={faFileMedical} />
                  Open Stale Checker
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardHome_v3;
